import type { CustomValidator } from '@rjsf/utils';
import type { CampaignConfigProviderCampaignFragment } from 'generated/graphql';
import type { ContentSectionConfig } from '../../../campaign-configs/types';
import type { TContent, TFormValues } from '../types';
import { ConfigDrivenContentEditor } from './ConfigDrivenContentEditor';
import { validateFlavorContent } from './validate-flavor-content';

/**
 * Deserializes the customValidate function stored as a string in the DDB campaign configuration.
 * This function uses dynamic evaluation to convert the JS string back into an executable function.
 *
 * @param config - The campaign configuration containing the customValidate string
 * @returns The deserialized custom validation function, or undefined if deserialization fails or no string is provided
 *
 * @example
 * ```typescript
 * const config = {
 *   contentConfiguration: {
 *     customValidate: "(function(exports) { exports.customValidate = (formData, errors) => errors; return exports; })({})"
 *   }
 * };
 * const validator = buildCustomValidate(config);
 * ```
 */
const buildCustomValidate = (config: CampaignConfigProviderCampaignFragment): CustomValidator | undefined => {
  const { customValidate: customValidateString } = config.contentConfiguration ?? {};
  if (!customValidateString) return;
  try {
    return new Function(`return ${customValidateString}`)().customValidate;
  } catch (error) {
    console.error('Failed to deserialize customValidate function:', error);
    return;
  }
};

export const buildContentConfig = (config: CampaignConfigProviderCampaignFragment): ContentSectionConfig<TContent, TFormValues> => ({
  contentConfiguration: {
    ...config.contentConfiguration,
    customValidate: buildCustomValidate(config),
  },
  FlavorContentEditor: ConfigDrivenContentEditor,
  validateFlavorContent,
});
